// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The selection bar's modify commands (frontend-u0o.4, slice S4; design.md
 * §4, Revit's two-letter codes) as `POST /model/edits` batches:
 *
 *  - Del (SL) at a clicked point → `wall.split`; Sammenføj (SF) two
 *    collinear walls → `wall.merge`; Saml hjørne (JN) two walls →
 *    `wall.join`. The server computes these (openings re-hosted, joins
 *    moved); here they are checked first, so a refusal reads in Danish
 *    before any request (the same limits as ReUseX `wall_edit_ops`).
 *  - Forlæng / trim (TR), Forskyd (OF), Juster (AL) and the transforms
 *    Flyt / Kopiér / Rotér / Spejl (MV CO RO MM) are computed here as
 *    `*.update` / `*.create` ops, one batch per command (one undo). Which
 *    of them go to the server's own ops once it has them is decided in one
 *    place: `modifyAdapter.ts`.
 *
 * Openings keep their place on a wall whose start moves along its axis
 * (their `s` is measured from the start). Walls joined to a moved one
 * follow it (a corner's end moves along, a tee's end slides along its own
 * line), as with the S3 grips. World x/y. Pure: no React, no DOM.
 */

import type { ModelEditOp, SolidOpening, SolidSpace, SolidWall } from '../api/types';
import type { GeometriModel } from './geometri';
// '.ts': mock-ruxd runs this module under node's type stripping (it applies the
// server's CAD ops with it), which needs the extensions — and only these imports.
import { lineIntersection, nearestOnSegment } from './inference.ts';
import type { XY } from './planWalls';
import { endingOn, frame, moveWallOps } from './wallGrips.ts';

/** A batch to commit, or why not (Danish, for the toast). */
export type OpsResult = { ops: ModelEditOp[] } | { error: string };

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const crs = (a: XY, b: XY) => a[0] * b[1] - a[1] * b[0];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const r6 = (v: number) => Math.round(v * 1e6) / 1e6 + 0;
const pt = (p: XY): [number, number] => [r6(p[0]), r6(p[1])];
const DEG = Math.PI / 180;

/** Shorter than this (metres) is no wall (ReUseX `split_wall`'s `min_part_m`). */
export const MIN_PART = 0.05;

type Found = { kind: 'wall'; row: SolidWall } | { kind: 'opening'; row: SolidOpening } | { kind: 'space'; row: SolidSpace };

/** The wall, opening or space `guid` (geometri's findElement without its imports). */
function findElement(model: Pick<GeometriModel, 'walls' | 'openings' | 'spaces'>, guid: string): Found | null {
  const w = model.walls?.find((x) => x.guid === guid);
  if (w) return { kind: 'wall', row: w };
  const o = model.openings?.find((x) => x.guid === guid);
  if (o) return { kind: 'opening', row: o };
  const sp = model.spaces?.find((x) => x.guid === guid);
  return sp ? { kind: 'space', row: sp } : null;
}

function wallOf(model: GeometriModel, guid: string): SolidWall | null {
  return model.walls?.find((w) => w.guid === guid) ?? null;
}

const fail = (error: string): OpsResult => ({ error });

// ------------------------------------------------------------ split --

/** Del (SL): wall `guid` cut where `p` projects on its axis; `newGuid` names the second part. */
export function splitOps(model: GeometriModel, guid: string, p: XY, newGuid?: string): OpsResult {
  const w = wallOf(model, guid);
  const f = w ? frame(w) : null;
  if (!w || !f) return fail('Vælg en væg at dele');
  const s = dot(sub(p, w.start), f.dir);
  if (!(s >= MIN_PART && s <= f.length - MIN_PART)) return fail('Klik inde på væggen — mindst 5 cm fra enderne');
  return { ops: [{ op: 'wall.split', guid, s: r6(s), ...(newGuid ? { new_guid: newGuid } : {}) }] };
}

/** Where a split at `p` falls (the cut point on the axis), for the preview; null off the wall. */
export function splitPoint(w: SolidWall, p: XY): XY | null {
  const f = frame(w);
  if (!f) return null;
  const s = dot(sub(p, w.start), f.dir);
  return s >= MIN_PART && s <= f.length - MIN_PART ? add(w.start, mul(f.dir, s)) : null;
}

// ------------------------------------------------------- merge / join --

/** ReUseX `WallMergeOptions`: parallel within 10°, each axis within 0,30 m of the other. */
const MERGE_ANGLE = 10;
const MERGE_OFFSET = 0.3;

/** Sammenføj (SF): two collinear walls on one storey become one (`b` goes). */
export function mergeOps(model: GeometriModel, a: string, b: string): OpsResult {
  const wa = wallOf(model, a);
  const wb = wallOf(model, b);
  const fa = wa ? frame(wa) : null;
  const fb = wb ? frame(wb) : null;
  if (!wa || !wb || !fa || !fb || a === b) return fail('Vælg to vægge at sammenføje');
  if (wa.storey_id !== wb.storey_id) return fail('Væggene står på hver sin etage');
  const angle = Math.acos(Math.min(1, Math.abs(dot(fa.dir, fb.dir)))) / DEG;
  if (angle > MERGE_ANGLE) return fail('Væggene ligger ikke på linje');
  const ma = mul(add(wa.start, wa.end), 0.5);
  const mb = mul(add(wb.start, wb.end), 0.5);
  const off = Math.max(Math.abs(dot(sub(mb, ma), fa.normal)), Math.abs(dot(sub(ma, mb), fb.normal)));
  if (off > MERGE_OFFSET) return fail(`Væggene ligger ${off.toFixed(2).replace('.', ',')} m fra hinanden på tværs`);
  return { ops: [{ op: 'wall.merge', guids: [a, b] }] };
}

/** ReUseX `join_walls`: not parallel within 5°; an end moves when the crossing lies at most 0,5 m inside it. */
const JOIN_MIN_ANGLE = 5;
const JOIN_MAX_TRIM = 0.5;

/** Saml hjørne (JN): both walls extended / trimmed to where their axes cross (a corner, or a tee onto one). */
export function joinOps(model: GeometriModel, a: string, b: string): OpsResult {
  const wa = wallOf(model, a);
  const wb = wallOf(model, b);
  const fa = wa ? frame(wa) : null;
  const fb = wb ? frame(wb) : null;
  if (!wa || !wb || !fa || !fb || a === b) return fail('Vælg to vægge at samle');
  if (wa.storey_id !== wb.storey_id) return fail('Væggene står på hver sin etage');
  if (Math.abs(crs(fa.dir, fb.dir)) < Math.sin(JOIN_MIN_ANGLE * DEG)) return fail('Væggene er parallelle — brug Sammenføj');
  const x = lineIntersection(wa.start, fa.dir, wb.start, fb.dir)!;
  const deep = (w: SolidWall, f: NonNullable<ReturnType<typeof frame>>) => {
    const s = dot(sub(x, w.start), f.dir);
    return Math.min(s, f.length - s) > JOIN_MAX_TRIM;
  };
  if (deep(wa, fa) && deep(wb, fb)) return fail('Væggene krydser hinanden — del en af dem først');
  return { ops: [{ op: 'wall.join', guids: [a, b] }] };
}

// ------------------------------------------- openings on a moved start --

/** Openings of `w` re-measured for a wall whose start moved `ds` along its axis and that is now `length` long. */
function shiftOpenings(model: GeometriModel, w: SolidWall, ds: number, length: number): OpsResult {
  const ops: ModelEditOp[] = [];
  for (const o of model.openings ?? []) {
    if (o.wall_guid !== w.guid) continue;
    const s0 = Math.min(o.s_start, o.s_end) - ds;
    const s1 = Math.max(o.s_start, o.s_end) - ds;
    if (s1 <= 0 || s0 >= length) return fail('En åbning sidder på den del, der fjernes — flyt eller slet den først');
    if (Math.abs(ds) < 1e-9) continue;
    ops.push({ op: 'opening.update', guid: o.guid, set: { s_start: r6(Math.max(0, s0)), s_end: r6(Math.min(length, s1)) } });
  }
  return { ops };
}

// ------------------------------------------------------ trim / extend --

/**
 * Forlæng / trim (TR): which end of wall `guid` goes to the axis of
 * `boundary`. Beyond its ends the nearer end extends; crossing it, the part
 * on the side of `keep` (the clicked point) stays and the other end comes
 * back to the boundary.
 */
export function extendEnd(model: GeometriModel, boundary: string, guid: string, keep: XY): { end: 'start' | 'end' } | { error: string } {
  const wb = wallOf(model, boundary);
  const w = wallOf(model, guid);
  const fb = wb ? frame(wb) : null;
  const f = w ? frame(w) : null;
  if (!wb || !w || !fb || !f || boundary === guid) return { error: 'Klik en anden væg end grænsen' };
  if (Math.abs(crs(f.dir, fb.dir)) < Math.sin(3 * DEG)) return { error: 'Væggen er parallel med grænsen' };
  const x = lineIntersection(w.start, f.dir, wb.start, fb.dir)!;
  const s = dot(sub(x, w.start), f.dir);
  if (s <= 0) return { end: 'start' };
  if (s >= f.length) return { end: 'end' };
  return { end: dot(sub(keep, w.start), f.dir) < s ? 'end' : 'start' };
}

/** End `end` of wall `guid` moved to the axis of `boundary` (which stays); its openings keep their place. */
export function extendEndOps(model: GeometriModel, boundary: string, guid: string, end: 'start' | 'end'): OpsResult {
  const wb = wallOf(model, boundary);
  const w = wallOf(model, guid);
  const fb = wb ? frame(wb) : null;
  const f = w ? frame(w) : null;
  if (!wb || !w || !fb || !f || boundary === guid) return fail('Klik en anden væg end grænsen');
  const x = lineIntersection(w.start, f.dir, wb.start, fb.dir);
  if (!x) return fail('Væggen er parallel med grænsen');
  const start = end === 'start' ? x : w.start;
  const stop = end === 'end' ? x : w.end;
  const length = dist(start, stop);
  if (length < MIN_PART) return fail('Væggen ville blive for kort');
  const ds = end === 'start' ? dot(sub(x, w.start), f.dir) : 0;
  const openings = shiftOpenings(model, w, ds, length);
  if ('error' in openings) return openings;
  return { ops: [{ op: 'wall.update', guid, set: { [end]: pt(x) } }, ...openings.ops] };
}

/** Forlæng / trim (TR) computed here: `extendEnd` then `extendEndOps`. */
export function extendToOps(model: GeometriModel, boundary: string, guid: string, keep: XY): OpsResult {
  const e = extendEnd(model, boundary, guid, keep);
  return 'error' in e ? e : extendEndOps(model, boundary, guid, e.end);
}

// ------------------------------------------------------------ offset --

/** Forskyd (OF): a copy of wall `guid`, `offset` metres along its left normal (its openings stay behind). */
export function offsetCopyOps(model: GeometriModel, guid: string, offset: number, newGuid: string): OpsResult {
  const w = wallOf(model, guid);
  const f = w ? frame(w) : null;
  if (!w || !f) return fail('Vælg en væg at forskyde');
  if (Math.abs(offset) < 0.01) return fail('Afstanden er for lille');
  const shift = mul(f.normal, offset);
  return { ops: [{ op: 'wall.create', record: { ...wallRecord(w), guid: newGuid, start: pt(add(w.start, shift)), end: pt(add(w.end, shift)) } }] };
}

/** The fields of a wall a create sends (world, the GET names). */
function wallRecord(w: SolidWall): Record<string, unknown> {
  return {
    storey_id: w.storey_id,
    start: pt(w.start),
    end: pt(w.end),
    z_base: w.z_base,
    z_top: w.z_top,
    height: r6(w.z_top - w.z_base),
    thickness: w.thickness,
    is_external: w.is_external,
    assumed: w.assumed,
  };
}

// ------------------------------------------------------------- align --

/** A line to align to: a wall's axis, or a plane trace (a face). */
export interface AlignReference {
  a: XY;
  b: XY;
  /** A guide line (S9) counts as an axis, as a wall's. */
  source: 'wall' | 'trace' | 'guide';
  /** The reference wall (left out of the move). */
  guid?: string;
}

/**
 * Juster (AL): wall `guid` moved across so that it lies on the reference
 * line — its axis on a wall's axis, its nearer face on a trace (a trace is
 * a scanned face). Walls joined to it follow (as the move grip). It must
 * run parallel to the reference (within 3°).
 */
export function alignOps(model: GeometriModel, ref: AlignReference, guid: string): OpsResult {
  const w = wallOf(model, guid);
  const f = w ? frame(w) : null;
  const rd = sub(ref.b, ref.a);
  const rl = Math.hypot(rd[0], rd[1]);
  if (!w || !f || !(rl > 1e-9) || ref.guid === guid) return fail('Klik den væg, der skal justeres');
  if (Math.abs(crs(f.dir, mul(rd, 1 / rl))) > Math.sin(3 * DEG)) return fail('Væggen er ikke parallel med referencen');
  let offset = dot(sub(ref.a, w.start), f.normal);
  if (ref.source === 'trace') offset -= Math.sign(offset || 1) * ((w.thickness ?? 0) / 2);
  if (Math.abs(offset) < 5e-4) return fail('Væggen ligger allerede på linjen');
  return { ops: moveWallOps(model, guid, offset) };
}

// -------------------------------------------------------- transforms --

/** p' = m·p + t, m row-major [a b; c d]. */
export interface Affine {
  m: [number, number, number, number];
  t: XY;
}

export const IDENTITY: Affine = { m: [1, 0, 0, 1], t: [0, 0] };

export function apply(T: Affine, p: XY): XY {
  return [T.m[0] * p[0] + T.m[1] * p[1] + T.t[0], T.m[2] * p[0] + T.m[3] * p[1] + T.t[1]];
}

export function translation(v: XY): Affine {
  return { m: [1, 0, 0, 1], t: v };
}

/** Counter-clockwise by `angle` radians about `pivot`. */
export function rotation(pivot: XY, angle: number): Affine {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const m: Affine['m'] = [c, -s, s, c];
  return { m, t: sub(pivot, [c * pivot[0] - s * pivot[1], s * pivot[0] + c * pivot[1]]) };
}

/** Reflection in the line through `a` and `b`; null when they coincide. */
export function reflection(a: XY, b: XY): Affine | null {
  const d = sub(b, a);
  const L = Math.hypot(d[0], d[1]);
  if (!(L > 1e-9)) return null;
  const u: XY = [d[0] / L, d[1] / L];
  const m: Affine['m'] = [2 * u[0] * u[0] - 1, 2 * u[0] * u[1], 2 * u[0] * u[1], 2 * u[1] * u[1] - 1];
  return { m, t: sub(a, [m[0] * a[0] + m[1] * a[1], m[2] * a[0] + m[3] * a[1]]) };
}

const isIdentity = (T: Affine) => Math.abs(T.m[0] - 1) + Math.abs(T.m[1]) + Math.abs(T.m[2]) + Math.abs(T.m[3] - 1) + Math.abs(T.t[0]) + Math.abs(T.t[1]) < 1e-9;

export interface TransformOptions {
  /** Leave the originals and add transformed copies (Kopiér, or Kopi on Rotér / Spejl). */
  copy: boolean;
  /** The guid for the copy of `original`. */
  newGuid: (original: string) => string;
}

/**
 * Flyt / Kopiér / Rotér / Spejl over a selection: the selected walls and
 * spaces transformed (`*.update`), or copied (`*.create`, the copied
 * walls' openings with them). A moved wall's openings ride along (their
 * `s` is along the host); an opening selected without its host slides
 * along the host by the move's component along it (Flyt only). Walls not
 * selected but joined to a moved one follow: an end at its end moves with
 * it (the corner kept), an end on its body slides along its own line to
 * the moved axis (the tee kept).
 */
export function transformOps(model: GeometriModel, guids: readonly string[], T: Affine, opts: TransformOptions): OpsResult {
  if (isIdentity(T)) return { ops: [] };
  const walls: SolidWall[] = [];
  const spaces: SolidSpace[] = [];
  const loose: SolidOpening[] = [];
  for (const g of guids) {
    const f = findElement(model, g);
    if (f?.kind === 'wall') walls.push(f.row);
    else if (f?.kind === 'space') spaces.push(f.row);
    else if (f?.kind === 'opening') loose.push(f.row);
  }
  if (walls.length + spaces.length + loose.length === 0) return fail('Vælg vægge, rum eller åbninger');
  const ops: ModelEditOp[] = [];
  const moved = new Set(walls.map((w) => w.guid));
  const tp = (p: XY) => pt(apply(T, p));
  const ring = (r: readonly (readonly [number, number])[]) => r.map((p) => tp(p as XY));

  if (opts.copy) {
    for (const w of walls) {
      const guid = opts.newGuid(w.guid);
      ops.push({ op: 'wall.create', record: { ...wallRecord(w), guid, start: tp(w.start), end: tp(w.end) } });
      for (const o of model.openings ?? []) {
        if (o.wall_guid !== w.guid) continue;
        ops.push({
          op: 'opening.create',
          record: { guid: opts.newGuid(o.guid), wall_guid: guid, kind: o.kind, state: o.state, s_start: o.s_start, s_end: o.s_end, z_sill: o.z_sill, z_head: o.z_head, is_external: o.is_external },
        });
      }
    }
    for (const s of spaces) {
      ops.push({
        op: 'space.create',
        record: {
          guid: opts.newGuid(s.guid),
          storey_id: s.storey_id,
          name: s.name,
          kind: s.kind,
          polygon: { outer: ring(s.polygon.outer), holes: s.polygon.holes.map(ring) },
          z_floor: s.z_floor,
          z_ceiling: s.z_ceiling,
        },
      });
    }
    return ops.length > 0 ? { ops } : fail('Der er intet at kopiere');
  }

  for (const w of walls) ops.push({ op: 'wall.update', guid: w.guid, set: { start: tp(w.start), end: tp(w.end) } });
  for (const s of spaces) {
    ops.push({ op: 'space.update', guid: s.guid, set: { polygon: { outer: ring(s.polygon.outer), holes: s.polygon.holes.map(ring) } } });
  }
  // A lone opening slides along its host (only a translation means anything along a fixed host).
  const pure = Math.abs(T.m[0] - 1) + Math.abs(T.m[1]) + Math.abs(T.m[2]) + Math.abs(T.m[3] - 1) < 1e-9;
  for (const o of loose) {
    if (moved.has(o.wall_guid)) continue;
    const host = wallOf(model, o.wall_guid);
    const f = host ? frame(host) : null;
    if (!host || !f || !pure) continue;
    const ds = dot(T.t, f.dir);
    const lo = Math.min(o.s_start, o.s_end);
    const hi = Math.max(o.s_start, o.s_end);
    const shift = Math.max(-lo, Math.min(f.length - hi, ds));
    if (Math.abs(shift) < 1e-6) continue;
    ops.push({ op: 'opening.update', guid: o.guid, set: { s_start: r6(lo + shift), s_end: r6(hi + shift) } });
  }

  // Walls joined to a moved one follow it.
  const all = model.walls ?? [];
  const follow = new Map<string, Record<string, unknown>>();
  for (const w of walls) {
    const f = frame(w);
    if (!f) continue;
    const ns = apply(T, w.start);
    const ne = apply(T, w.end);
    const nd = sub(ne, ns);
    for (const t of endingOn(all, w)) {
      if (moved.has(t.wall.guid)) continue;
      const p = t.wall[t.end];
      const at = dot(sub(p, w.start), f.dir);
      const reach = Math.max(w.thickness ?? 0, t.wall.thickness ?? 0) / 2 + 0.02;
      let to: XY | null;
      if (at <= reach || at >= f.length - reach) to = apply(T, p);
      else {
        const od = sub(t.wall.end, t.wall.start);
        to = lineIntersection(t.wall.start, od, ns, nd);
      }
      if (!to) continue;
      follow.set(t.wall.guid, { ...(follow.get(t.wall.guid) ?? {}), [t.end]: pt(to) });
    }
  }
  for (const [guid, set] of follow) ops.push({ op: 'wall.update', guid, set });
  return { ops };
}

/** The centre of a selection's bounding box (Rotér's default pivot); null when nothing placed is selected. */
export function selectionCentre(model: GeometriModel, guids: readonly string[]): XY | null {
  let lo: XY = [Infinity, Infinity];
  let hi: XY = [-Infinity, -Infinity];
  const grow = (p: readonly number[]) => {
    lo = [Math.min(lo[0], p[0]), Math.min(lo[1], p[1])];
    hi = [Math.max(hi[0], p[0]), Math.max(hi[1], p[1])];
  };
  for (const g of guids) {
    const f = findElement(model, g);
    if (f?.kind === 'wall') [f.row.start, f.row.end].forEach(grow);
    else if (f?.kind === 'space') f.row.polygon.outer.forEach(grow);
    else if (f?.kind === 'opening' && f.row.start && f.row.end) [f.row.start, f.row.end].forEach(grow);
  }
  return Number.isFinite(lo[0]) ? [(lo[0] + hi[0]) / 2, (lo[1] + hi[1]) / 2] : null;
}

/** The point of wall `w`'s axis nearest `p` (where a click on it lands). */
export function onWall(w: SolidWall, p: XY): XY {
  return nearestOnSegment(w.start, w.end, p).point;
}

// ------------------------------------------------- the server's CAD ops --

/**
 * The server's CAD ops (`wall.extend_to`, `selection.transform`,
 * `wall.offset`, ruxd-qfc) as the `*.update` / `*.create` ops they come to
 * here: the optimistic overlay draws a batch with them before the server's
 * answer, and mock-ruxd applies them with them. An approximation of the
 * server's own result (it re-solves joins and may end a tee on the
 * target's near face); the re-read replaces it. Null for any other op.
 */
export function expandServerOp(model: GeometriModel, op: ModelEditOp): OpsResult | null {
  switch (op.op) {
    case 'wall.extend_to':
      return extendEndOps(model, op.target, op.guid, op.end);
    case 'wall.offset': {
      const w = wallOf(model, op.guid);
      const f = w ? frame(w) : null;
      if (!w || !f) return fail(`Ingen væg ${op.guid}`);
      if (op.copy === false) return transformOps(model, [op.guid], translation(mul(f.normal, op.distance)), { copy: false, newGuid: (g) => g });
      return offsetCopyOps(model, op.guid, op.distance, op.new_guid ?? `${op.guid}-offset`);
    }
    case 'selection.transform': {
      const r = rotation(op.pivot ?? [0, 0], ((op.rotate_deg ?? 0) * Math.PI) / 180);
      const t = op.translate ?? [0, 0];
      const T: Affine = { m: r.m, t: [r.t[0] + t[0], r.t[1] + t[1]] };
      const names = op.new_guids ?? {};
      return transformOps(model, op.guids, T, { copy: op.copy === true, newGuid: (g) => names[g] ?? `${g}-copy` });
    }
    default:
      return null;
  }
}
